/**
 * Returns an array of common modules for use in standalone components.
 * Replaces SharedModule for more flexible and explicit importing.
 */

import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HttpClientModule } from '@angular/common/http';
import { NgxMaskDirective, NgxMaskPipe, provideEnvironmentNgxMask } from 'ngx-mask';

// Import ng-zorro components individually for better performance
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzMessageModule } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzNotificationModule } from 'ng-zorro-antd/notification';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzResultModule } from 'ng-zorro-antd/result';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzTagModule } from 'ng-zorro-antd/tag';

// Additional NG-Zorro modules
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTypographyModule } from 'ng-zorro-antd/typography';

// Basic Angular modules, always used
export const COMMON_ANGULAR_IMPORTS = [
  CommonModule,
  ReactiveFormsModule,
  FormsModule,
  RouterModule,
  HttpClientModule,
];

// Mask for phones, email, etc.
export const MASK_IMPORTS = [NgxMaskDirective, NgxMaskPipe];

// Providers for NgxMask
export const MASK_PROVIDERS = [provideEnvironmentNgxMask()];

// Core Ant Design components
export const NG_ZORRO_IMPORTS = [
  NzButtonModule,
  NzFormModule,
  NzInputModule,
  NzSelectModule,
  NzIconModule,
  NzGridModule,
  NzLayoutModule,
  NzTypographyModule,
  NzAvatarModule,
  NzTableModule,
  NzDividerModule,
  NzCardModule,
  NzModalModule,
  NzNotificationModule,
  NzCheckboxModule,
  NzDatePickerModule,
  NzTabsModule,
  NzUploadModule,
  NzPopconfirmModule,
  NzAlertModule,
  NzSpinModule,
  NzMessageModule,
  NzResultModule,
  NzEmptyModule,
  NzProgressModule,
  NzTagModule,
];

// Table and data components
export const ANTD_DATA_IMPORTS = [
  NzTableModule,
  NzDropDownModule,
  NzMenuModule,
  NzCardModule,
  NzListModule,
  NzDescriptionsModule,
  NzTagModule,
];

// Form and input related components
export const ANTD_FORM_IMPORTS = [
  NzFormModule,
  NzInputModule,
  NzSelectModule,
  NzCheckboxModule,
  NzDatePickerModule,
  NzAutocompleteModule,
  NzUploadModule,
  NzSwitchModule,
  NzStepsModule,
];

// Feedback components
export const FEEDBACK_IMPORTS = [
  NzAlertModule,
  NzMessageModule,
  NzNotificationModule,
  NzProgressModule,
  NzSpinModule,
];

// Ready-made import sets for different component types
export const FORM_IMPORTS = [...COMMON_ANGULAR_IMPORTS, ...ANTD_FORM_IMPORTS, ...MASK_IMPORTS];

export const DATA_IMPORTS = [...COMMON_ANGULAR_IMPORTS, ...NG_ZORRO_IMPORTS, ...ANTD_DATA_IMPORTS];

export const LAYOUT_IMPORTS = [
  ...COMMON_ANGULAR_IMPORTS,
  NzLayoutModule,
  NzGridModule,
  NzMenuModule,
  NzIconModule,
  NzDividerModule,
];

export const ALL_IMPORTS = [
  ...COMMON_ANGULAR_IMPORTS,
  ...NG_ZORRO_IMPORTS,
  ...ANTD_DATA_IMPORTS,
  ...FEEDBACK_IMPORTS,
  ...ANTD_FORM_IMPORTS,
  ...MASK_IMPORTS,
  NzDividerModule,
];
